import { useCallback, useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  MenuBook as JournalIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { PDFViewer } from "@react-pdf/renderer";

import { useAuth } from "@/features/authentication";
import { SalesInvoicePdf, StatusEnum } from "@/features/sales-invoices";
import { PermissionEnum } from "@/features/permissions";
import {
  DownPaymentAllocationsSection,
  ExpensesSection,
  GeneralInformationSection,
  ProductVariantsSection,
} from "@/features/sales-invoices/sales-invoice";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import {
  CenteredLoading,
  JournalDetailsDialog,
  PageContainer,
  ResponsiveToolbarButton,
  TabLabelWithCount,
} from "@/features/ui";

export const SalesInvoice = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { outlet, user, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [salesInvoice, setSalesInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [journalDialogOpen, setJournalDialogOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const loadSalesInvoice = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    setError(null);
    try {
      const {
        data: { data },
      } = await apiOutletClient.get(`sales-invoices/${referenceNumber}`, {
        params: {
          withBuyer: true,
          withSeller: true,
          withPaymentTerm: true,
          withProductVariants: true,
          withTransactionHistories: true,
          withSalesInvoiceExpenses: true,
          withSalesDownPaymentAllocations: true,
          withCreator: true,
        },
      });
      setSalesInvoice(data);
    } catch (error) {
      setError(
        getErrorMessage(error, "Gagal memuat faktur penjualan"),
      );
    } finally {
      setLoading(false);
    }
  }, [outlet, referenceNumber]);

  useEffect(() => {
    loadSalesInvoice();
  }, [loadSalesInvoice]);

  if (loading) {
    return <CenteredLoading sx={{ height: "100vh" }} />;
  }

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  const toolbarContent = (
    <>
      {hasPermission(PermissionEnum.UPDATE_SALES_INVOICES) &&
        salesInvoice.status === StatusEnum.UNPAID && (
          <ResponsiveToolbarButton
            label="Ubah"
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate("edit")}
          />
        )}
      <ResponsiveToolbarButton
        label="Rincian Jurnal"
        variant="outlined"
        startIcon={<JournalIcon />}
        onClick={() => setJournalDialogOpen(true)}
      />
      <ResponsiveToolbarButton
        label="Cetak PDF"
        variant="outlined"
        startIcon={<PdfIcon />}
        onClick={() => setPdfDialogOpen(true)}
      />
    </>
  );

  return (
    <PageContainer
      maxWidth="xl"
      title="Faktur Penjualan"
      breadcrumbs={[
        { title: "Faktur Penjualan", path: "/sales/sales-invoices" },
        { title: referenceNumber },
      ]}
      slotProps={{
        header: { slotProps: { toolbar: { children: toolbarContent } } },
      }}
    >
      <Stack spacing={2}>
        <GeneralInformationSection salesInvoice={salesInvoice} />

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, newValue) => setTabIndex(newValue)}
          >
            <Tab
              label={
                <TabLabelWithCount
                  label="Faktur"
                  count={salesInvoice.salesInvoiceProductVariants?.length ?? 0}
                />
              }
            />
            <Tab
              label={
                <TabLabelWithCount
                  label="Biaya Lain-lain"
                  count={salesInvoice.salesInvoiceExpenses?.length ?? 0}
                />
              }
            />
            <Tab
              label={
                <TabLabelWithCount
                  label="Uang Muka"
                  count={
                    salesInvoice.salesDownPaymentAllocations?.length ?? 0
                  }
                />
              }
            />
          </Tabs>
          <Divider sx={{ mb: 2.5 }} />

          {tabIndex === 0 && (
            <ProductVariantsSection
              salesInvoiceProductVariants={
                salesInvoice.salesInvoiceProductVariants
              }
            />
          )}
          {tabIndex === 1 && (
            <ExpensesSection
              salesInvoiceExpenses={salesInvoice.salesInvoiceExpenses}
            />
          )}
          {tabIndex === 2 && (
            <DownPaymentAllocationsSection
              salesDownPaymentAllocations={
                salesInvoice.salesDownPaymentAllocations
              }
            />
          )}
        </Paper>

        {/* PDF Dialog */}
        <Dialog
          open={pdfDialogOpen}
          onClose={() => setPdfDialogOpen(false)}
          maxWidth="lg"
          fullWidth
          slotProps={{
            paper: {
              sx: { height: "90vh" },
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Faktur Penjualan - {referenceNumber}
            <IconButton onClick={() => setPdfDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, height: "100%" }}>
            {salesInvoice && user && (
              <PDFViewer width="100%" height="100%">
                <SalesInvoicePdf
                  salesInvoice={{
                    ...salesInvoice,
                    salesInvoiceProductVariants: [
                      ...salesInvoice.salesInvoiceProductVariants,
                    ].sort((a, b) => a.name.localeCompare(b.name)),
                  }}
                  user={user}
                />
              </PDFViewer>
            )}
          </DialogContent>
        </Dialog>
        {/* Journal Details Dialog */}
        <JournalDetailsDialog
          open={journalDialogOpen}
          onClose={() => setJournalDialogOpen(false)}
          transactionableType="SALES_INVOICE"
          transactionableId={salesInvoice?.id}
        />
      </Stack>
    </PageContainer>
  );
};
